import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { mascaraCie, CIE_FRECUENTES, CIE_RE } from './femoDef';

/* Bloque K del FEMO: diagnósticos CIE-10 con búsqueda automática.
   Se escribe el código (M54.5) o el nombre (lumbago, «dolor lumbar»…) y aparece la lista del catálogo CIE-10 en español;
   al elegir uno se llenan solos el código y la descripción. Hasta 6 diagnósticos. */

export const css = `
.fx-dx{display:grid;grid-template-columns:130px 1fr 150px 38px;gap:8px;align-items:start;}
.fx-dxh{font-size:10.5px;text-transform:uppercase;color:var(--text3);font-weight:700;padding:0 2px;}
.fx-dxc{position:relative;min-width:0;}
.fx-sug{position:absolute;z-index:30;left:0;right:0;top:calc(100% + 3px);min-width:300px;max-height:270px;overflow:auto;background:#fff;border:1px solid var(--border);border-radius:10px;box-shadow:0 8px 24px rgba(20,33,61,.16);padding:4px;}
.fx-sug button{display:flex;gap:10px;align-items:baseline;width:100%;text-align:left;border:0;background:none;padding:7px 9px;border-radius:7px;cursor:pointer;font:inherit;font-size:12.5px;color:var(--text);}
.fx-sug button.on,.fx-sug button:hover{background:var(--blue-lt);}
.fx-sug button[disabled]{opacity:.45;cursor:not-allowed;}
.fx-sug b{font-family:ui-monospace,Consolas,monospace;font-size:12px;color:var(--navy);flex:none;min-width:52px;}
.fx-sug small{color:var(--text3);font-size:10.5px;margin-left:auto;flex:none;}
.fx-sug .vacio{padding:9px 10px;font-size:12px;color:var(--text3);}
.fx-frec{display:flex;gap:6px;flex-wrap:wrap;align-items:center;font-size:11px;color:var(--text3);}
.fx-frec button{border:1px solid var(--border);background:#fff;border-radius:14px;padding:3px 10px;font-size:11.5px;cursor:pointer;color:var(--text2);}
.fx-frec button:hover:not([disabled]){background:var(--blue-lt);}
.fx-frec button[disabled]{opacity:.45;cursor:not-allowed;}
@media (max-width:760px){.fx-dx{grid-template-columns:1fr 38px;} .fx-dxh{display:none;} .fx-dx > .fx-dxc:nth-child(2),.fx-dx > .fx-dxc:nth-child(3){grid-column:1 / -1;} .fx-sug{min-width:0;}}
`;

const buscar = q => api.get(`/ocupacional/cie10?q=${encodeURIComponent(q)}`).then(r => (Array.isArray(r.data) ? r.data : []));

/** Campo con lista de sugerencias. `modo`: 'codigo' | 'texto'. */
function Buscador({ modo, valor, onTexto, onElegir, onSalir, usados, ph, id, maxLength }) {
  const [lista, setLista] = useState(null);                 // null = cerrada
  const [activo, setActivo] = useState(0);
  const reloj = useRef(null);
  const n = useRef(0);
  useEffect(() => () => clearTimeout(reloj.current), []);

  function pedir(q) {
    clearTimeout(reloj.current);
    const minimo = modo === 'codigo' ? 1 : 2;
    if (String(q).trim().length < minimo) { setLista(null); return; }
    const mio = ++n.current;                                 // si llega una respuesta vieja, se ignora
    reloj.current = setTimeout(() => {
      buscar(q).then(r => { if (mio === n.current) { setLista(r); setActivo(0); } }).catch(() => { if (mio === n.current) setLista([]); });
    }, 180);
  }
  const cerrar = () => { n.current++; clearTimeout(reloj.current); setLista(null); };
  const elegir = r => { if (usados.includes(r.codigo)) return; cerrar(); onElegir(r); };
  function tecla(e) {
    if (lista == null) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActivo(a => Math.min(a + 1, lista.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { if (lista[activo]) { e.preventDefault(); elegir(lista[activo]); } }
    else if (e.key === 'Escape') { cerrar(); }
  }
  return (
    <div className="fx-dxc">
      <input id={id} className="form-control" type="text" autoComplete="off" placeholder={ph} value={valor} maxLength={maxLength}
        style={modo === 'codigo' ? { fontFamily: 'ui-monospace,Consolas,monospace', fontWeight: 700, textTransform: 'uppercase' } : undefined}
        role="combobox" aria-expanded={lista != null} aria-autocomplete="list"
        onChange={e => { onTexto(e.target.value); pedir(modo === 'codigo' ? mascaraCie(e.target.value) : e.target.value); }}
        onKeyDown={tecla} onBlur={() => { setTimeout(cerrar, 120); if (onSalir) onSalir(); }} />
      {lista != null && (
        <div className="fx-sug" role="listbox">
          {lista.length === 0 && <div className="vacio">No hay coincidencias en la CIE-10. Pruebe con otra palabra o con el código.</div>}
          {lista.map((r, i) => (
            <button type="button" key={r.codigo} role="option" aria-selected={i === activo} className={i === activo ? 'on' : ''}
              disabled={usados.includes(r.codigo)} onMouseDown={e => { e.preventDefault(); elegir(r); }}>
              <b>{r.codigo}</b><span>{r.descripcion}</span>
              {usados.includes(r.codigo) ? <small>ya agregado</small> : r.categoria ? <small>grupo</small> : null}
            </button>))}
        </div>)}
    </div>
  );
}

export default function Diagnosticos({ filas, set, max = 6 }) {
  const codigos = filas.map(f => f.cie || '');
  const cambia = (i, cambio) => set(filas.map((f, j) => (j === i ? { ...f, ...cambio } : f)));
  const quitar = i => set(filas.filter((_, j) => j !== i));
  const agregar = (r) => set([...filas, { cie: r ? r.codigo : '', descripcion: r ? r.descripcion : '', tipo: '' }]);

  /** Si escribió el código completo a mano y la descripción está vacía, la trae del catálogo. */
  function alSalirDelCodigo(i) {
    const f = filas[i];
    if (!f || !CIE_RE.test(f.cie) || String(f.descripcion || '').trim()) return;
    api.get(`/ocupacional/cie10?codigo=${encodeURIComponent(f.cie)}`).then(r => { if (r.data && r.data.descripcion) set(filas.map((x, j) => (j === i && !String(x.descripcion || '').trim() ? { ...x, descripcion: r.data.descripcion } : x))); }).catch(() => {});
  }
  async function frecuente(c) {
    if (filas.length >= max || codigos.includes(c)) return;
    try { const r = await api.get(`/ocupacional/cie10?codigo=${encodeURIComponent(c)}`); agregar(r.data || { codigo: c, descripcion: '' }); } catch (e) { agregar({ codigo: c, descripcion: '' }); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="mo-sub">Escriba el código (M54.5) o el nombre (lumbago, hipertensión, hipoacusia…): la lista sale sola y al elegir se llena la descripción. Catálogo CIE-10 en español.</div>
      {filas.length > 0 && (
        <div className="fx-dx" aria-hidden="true"><div className="fx-dxh">CIE-10</div><div className="fx-dxh">Descripción</div><div className="fx-dxh">PRE / DEF</div><div /></div>)}
      {filas.map((f, i) => (
        <div className="fx-dx" key={i}>
          <Buscador modo="codigo" id={'fx-cie-' + i} valor={f.cie || ''} ph="M54.5" maxLength={6} usados={codigos.filter((c, j) => j !== i && c)}
            onTexto={v => cambia(i, { cie: mascaraCie(v) })} onElegir={r => cambia(i, { cie: r.codigo, descripcion: r.descripcion })} onSalir={() => alSalirDelCodigo(i)} />
          <Buscador modo="texto" id={'fx-desc-' + i} valor={f.descripcion || ''} ph="Buscar por nombre: lumbago, miopía…" usados={codigos.filter((c, j) => j !== i && c)}
            onTexto={v => cambia(i, { descripcion: v })} onElegir={r => cambia(i, { cie: r.codigo, descripcion: r.descripcion })} />
          <select className="form-control" value={f.tipo || ''} onChange={e => cambia(i, { tipo: e.target.value })} aria-label="Presuntivo o definitivo">
            <option value="">—</option><option value="PRE">Presuntivo</option><option value="DEF">Definitivo</option></select>
          <button type="button" className="btn btn-sm" title="Quitar diagnóstico" aria-label="Quitar diagnóstico" onClick={() => quitar(i)}>✕</button>
        </div>))}
      {filas.length < max && <div><button type="button" className="btn btn-sm" onClick={() => agregar(null)}>+ Diagnóstico</button></div>}
      {filas.length < max && (
        <div className="fx-frec"><span>Frecuentes:</span>
          {CIE_FRECUENTES.map(c => <button type="button" key={c} disabled={codigos.includes(c)} onClick={() => frecuente(c)}>{c}</button>)}</div>)}
    </div>
  );
}
